import React, { useContext } from 'react';
import styled from 'styled-components';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import useOnclickOutside from 'react-cool-onclickoutside';
import { MapContext } from '@com/core/common/contexts/mapContext';
import { reportError } from '@com/core/common/utils/sentry';

type TProps = {
    placeholder?: string;
};

const SearchBox = ({ placeholder }: TProps): JSX.Element => {
    const { handleMapClickDaily, searchText, updateSearchText } = useContext(MapContext);

    const {
        clearSuggestions,
        ready,
        setValue,
        suggestions: { data, status },
        value,
    } = usePlacesAutocomplete();

    const ref = useOnclickOutside(() => {
        // When user clicks outside of the component, we can dismiss
        // the searched suggestions by calling this method
        clearSuggestions();
    });

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        // Update the keyword of the input element
        updateSearchText(null);
        setValue(e.target.value);
    };

    const handleSelect = ({ description }: { description: string }) => (): void => {
        // When user selects a place, we can replace the keyword without request data from API
        // by setting the second parameter as "false"
        updateSearchText(null);
        setValue(description, false);
        clearSuggestions();

        // Get latitude and longitude via utility functions
        getGeocode({ address: description })
            .then(results => getLatLng(results[0]))
            .then(({ lat, lng }) => {
                handleMapClickDaily({ lat, lng });
            })
            .catch(error => {
                reportError(error);
            });
    };

    return (
        <SearchBoxContainer ref={ref}>
            <Input
                data-qa="tf_searchFieldMaps"
                placeholder={placeholder || 'Search location'}
                value={searchText || value}
                onChange={handleInput}
                disabled={!ready}
                type="text"
            />
            {status === 'OK' && data && data.length ? (
                <>
                    <SuggestionsContainer data-qa="suggestions_Maps">
                        {data.map(suggestion => (
                            <Suggestion key={suggestion.place_id} onClick={handleSelect(suggestion)}>
                                {suggestion.structured_formatting.main_text}{' '}
                                {suggestion.structured_formatting.secondary_text}
                            </Suggestion>
                        ))}
                    </SuggestionsContainer>
                </>
            ) : null}
        </SearchBoxContainer>
    );
};

const SearchBoxContainer = styled.div`
    position: absolute;
    top: 16px;
    left: 16px;
    right: 16px;
    z-index: 12;
`;

const Input = styled.input`
    border-width: 0;
    padding: 14px 16px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
`;

const SuggestionsContainer = styled.div`
    font-size: 0.9375rem;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    z-index: 12;
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
    position: absolute;
    width: 100%;
    margin-top: 4px;
    overflow: hidden;
`;

const Suggestion = styled.div`
    cursor: pointer;
    padding: 12px 16px;

    + div {
        border-top: 1px solid ${({ theme }): string => `${theme.colors.border}`};
    }

    &:hover {
        background-color: ${({ theme }): string => `${theme.colors.brandPrimaryLight}`};
    }
`;

export default SearchBox;
